<script setup lang="ts">
import { computed } from "vue";
import { navLinks, resolveExternalLinks, resolveMyPagesLinks } from "./navLinks";
import { useAuthenticatedUserStore } from "@/services/store";
import { useRoute, useRouter } from "vue-router";
import type { RouteLocationNamedRaw } from "vue-router";
import UserMenu from "@/modules/root/components/UserMenu.vue";

defineProps<{ modelValue: boolean }>();
const emit = defineEmits<{ (e: "update:modelValue", value: boolean): void }>();

const userStore = useAuthenticatedUserStore();
const router = useRouter();
const route = useRoute();

const supportUrl = "https://support.oleter.se/hc/sv";

function isChildActive(to: RouteLocationNamedRaw): boolean {
  if (typeof to !== "object" || !("name" in to) || route.name !== to.name) {
    return false;
  }
  if (!to.query) {
    return false;
  }
  return Object.entries(to.query).every(([key, val]) => String(route.query[key]) === String(val));
}

const userName = computed<string | null>(() => userStore.userName);

const visibleNavLink = computed(() => {
  const isForemanOrAbove = userStore.isForemanOrAbove;
  const isKma = userStore.isKma;
  const isCsc = userStore.isCustomerSalesCoordinator;

  const filteredLinks = navLinks.filter((x) => (!x.foremanOnly || isForemanOrAbove) && (!x.isKma || isKma));
  const isNewVersionAvailable = userStore.application.hasNewVersion;

  return filteredLinks.map((link) => {
    const to = { name: link.to };
    const visibleChildren = link.children?.filter((child) => !child.cscOnly || isCsc);

    return {
      title: link.title,
      icon: link.icon,
      chip: link.chip,
      ...(isNewVersionAvailable ? { to: undefined, href: router.resolve(to).href } : { to, href: undefined }),
      children: visibleChildren,
      hardLink: isNewVersionAvailable,
    };
  });
});

const externalLinks = resolveExternalLinks();

const myPagesLinks = computed(() => {
  const myLinks = resolveMyPagesLinks();
  return myLinks.filter((x) => !x.foremanOnly || userStore.isForemanOrAbove);
});

const activeCompany = computed(() => {
  return userStore.user.activeCompany;
});
</script>
<template>
  <VNavigationDrawer :model-value="modelValue" @update:model-value="(e) => emit('update:modelValue', e)">
    <VCard class="bg-primary" flat style="border-radius: 0">
      <template v-if="activeCompany" #title> {{ activeCompany.name }} </template>
      <template v-else #title>
        <img src="@/assets/images/icons/ocab-logo-white.svg" width="40%" alt="" style="border-radius: 4px"
      /></template>
      <template v-if="userName" #subtitle>{{ userName }}</template>
      <template #append>
        <VAvatar>
          <UserMenu />
        </VAvatar>
      </template>
    </VCard>

    <VList nav density="compact">
      <VListItem link :href="supportUrl" target="_blank">
        <template #prepend>
          <VIcon>mdi-message</VIcon>
        </template>
        <VListItemTitle>Support</VListItemTitle>
      </VListItem>

      <VDivider class="my-2" />

      <template v-for="(item, i) in visibleNavLink" :key="`${i}-${item.title}`">
        <VListItem link :to="item.to" :href="item.href" :exact="item.to?.name === 'home'">
          <template #prepend>
            <VIcon>{{ item.icon }}</VIcon>
          </template>
          <VListItemTitle>{{ item.title }}</VListItemTitle>
          <template v-if="item.chip" #append>
            <VChip size="small" :variant="item.chip.variant" :color="item.chip.color">
              <!-- Bold on the span: Vuetify's unlayered `.v-chip { font-weight }` beats Tailwind on the chip itself. -->
              <span class="font-semibold">{{ item.chip.label }}</span>
            </VChip>
          </template>
        </VListItem>
        <div v-if="item.children?.length" class="children-group">
          <VListItem
            v-for="(child, childIndex) in item.children"
            :key="childIndex"
            link
            :to="child.to"
            :active="isChildActive(child.to)"
          >
            <VListItemTitle class="text-wrap pl-7">{{ child.title }}</VListItemTitle>
          </VListItem>
        </div>
      </template>

      <VDivider class="my-2" />

      <VListGroup :value="false" expand-icon="mdi-chevron-right" collapse-icon="mdi-chevron-down">
        <template #activator="{ props }">
          <VListItem v-bind="props">
            <VListSubheader>Länkar</VListSubheader>
          </VListItem>
        </template>
        <VListItem
          v-for="link in myPagesLinks"
          :key="link.href"
          class="nav-drawer-external-link"
          link
          :href="link.href"
          target="_blank"
        >
          <template #prepend>
            <VIcon>{{ link.icon }}</VIcon>
          </template>
          <VListItemTitle>{{ link.title }}</VListItemTitle>
        </VListItem>
        <VListItem
          v-for="link in externalLinks"
          :key="link.href"
          class="nav-drawer-external-link"
          link
          :href="link.href"
          target="_blank"
        >
          <template #prepend>
            <VIcon>{{ link.icon }}</VIcon>
          </template>
          <VListItemTitle>{{ link.title }}</VListItemTitle>
        </VListItem>
      </VListGroup>
    </VList>
  </VNavigationDrawer>
</template>

<style scoped>
.children-group {
  padding-left: 8px;
  margin-left: 18px;
  border-left: 1px solid rgba(var(--v-theme-on-surface), 0.12);
}

.nav-drawer-external-link {
  padding-inline-start: 0 !important;
}
</style>
